import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { BookOpen, Users, ClipboardList, Calendar } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CARD_ACCENTS = ["#8B5CF6", "#0A7AFF", "#FF3636", "#22C55E"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0C0C10] border border-[#252530] px-3 py-2">
      <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#EAFF00] mb-1">{label}</p>
      <p className="font-['JetBrains_Mono'] text-sm text-[#EEEADF]">{payload[0].value}</p>
    </div>
  );
};

const timetable = [
  { time: "09:00 AM", subject: "Data Structures", batch: "SE-A", room: "B-201", students: 62 },
  { time: "11:00 AM", subject: "AI & Machine Learning", batch: "TE-B", room: "A-105", students: 58 },
  { time: "02:00 PM", subject: "DBMS Lab", batch: "SE-B", room: "Lab-3", students: 30 },
];

const assignments = [
  { course: "Data Structures", submissions: 48, total: 62, due: "Tomorrow" },
  { course: "AI & ML", submissions: 31, total: 58, due: "Mar 15" },
  { course: "DBMS Lab", submissions: 29, total: 30, due: "Mar 20" },
];

export default function FacultyDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const attendanceData = stats?.charts?.classAttendance || [
    { class: "DS301", pct: 85 }, { class: "AI402", pct: 78 },
    { class: "DB201", pct: 92 }, { class: "OS301", pct: 74 }, { class: "CN401", pct: 88 }
  ];

  const statCards = [
    { label: "My Courses", value: stats?.stats?.my_courses || 5, sub: "Active this sem" },
    { label: "Total Students", value: stats?.stats?.total_students || 243, sub: "Across all classes" },
    { label: "Pending Reviews", value: stats?.stats?.pending_assignments || 18, sub: "Assignments" },
    { label: "Leave Balance", value: `${stats?.stats?.leave_balance || 12}d`, sub: "Available days" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="faculty-dashboard" className="space-y-6">
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Faculty — Computer Science</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Welcome, {user?.name?.split(" ")[0]}.</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`faculty-stat-${i}`}
            className="bg-white border border-[#E2DDD4] p-5 hover:border-[#0C0C10] transition-colors"
            style={{ borderLeft: `3px solid ${CARD_ACCENTS[i]}` }}
          >
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-[2.2rem] text-[#0C0C10] leading-none">{s.value}</p>
            <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-3">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Today's timetable */}
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="faculty-timetable">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Today's Schedule</p>
          <div className="space-y-2">
            {timetable.map((cls, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#F5F2EA] border border-[#E2DDD4]">
                <div className="w-1 h-12 bg-[#8B5CF6] flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{cls.subject}</p>
                  <p className="font-['DM_Sans'] text-xs text-[#7A7468]">{cls.batch} — {cls.room} — {cls.students} students</p>
                </div>
                <span className="font-['JetBrains_Mono'] text-xs text-[#8B5CF6]">{cls.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Class attendance chart */}
        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Class Attendance</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0C0C10] mb-5">Average % by subject</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={attendanceData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
              <XAxis dataKey="class" tick={{ fontSize: 9, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="pct" fill="#8B5CF6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Assignment submissions */}
      <div className="bg-white border border-[#E2DDD4] p-6" data-testid="pending-assignments">
        <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-5">Assignment Submissions</p>
        <div className="space-y-5">
          {assignments.map((a, i) => (
            <div key={i}>
              <div className="flex justify-between mb-2">
                <span className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{a.course}</span>
                <div className="flex items-center gap-3">
                  <span className="font-['DM_Sans'] text-xs text-[#7A7468]">Due: {a.due}</span>
                  <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10] font-bold">{a.submissions}/{a.total}</span>
                </div>
              </div>
              <div className="w-full h-1.5 bg-[#F0ECE4]">
                <div
                  className="h-1.5 bg-[#8B5CF6] transition-all duration-500"
                  style={{ width: `${(a.submissions / a.total) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
